import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import {
  Search,
  Filter,
  Heart,
  Calendar,
  MapPin,
  Edit,
  Trash2,
  Plus,
  Camera,
  X,
} from "lucide-react";
import { deletePet } from "../../../services/api";
import { AuthContext } from "../../../context/AuthContext";

const AllPets = () => {
  // Get context functions and state
  const { getAllPet, allPets, allPetsLoading, allPetsError, totalPages } =
    useContext(AuthContext);

  // Store all pets and filtered pets separately
  const [allPetsData, setAllPetsData] = useState([]);
  const [filteredPets, setFilteredPets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    species: "",
    gender: "",
    breed: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Pagination settings
  const ITEMS_PER_PAGE = 12;

  // Debounce search to prevent too many filter operations
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Initial data fetch
  useEffect(() => {
    if (isInitialLoad) {
      fetchAllPets();
    }
  }, []);

  // Update filtered pets when allPets data changes
  useEffect(() => {
    if (allPets?.length > 0) {
      setAllPetsData(allPets);
      if (isInitialLoad) {
        setFilteredPets(allPets);
        setIsInitialLoad(false);
      }
    }
  }, [allPets, isInitialLoad]);

  // Apply filters when search term or filters change
  useEffect(() => {
    if (!isInitialLoad) {
      applyFilters();
    }
  }, [debouncedSearchTerm, filters]);

  // Reset page when filters change
  useEffect(() => {
    if (!isInitialLoad) {
      setPage(1);
    }
  }, [debouncedSearchTerm, filters]);

  // Fetch all pets from API (only once)
  const fetchAllPets = async () => {
    try {
      await getAllPet({ limit: 1000 }); // Get all pets at once
    } catch (error) {
      console.error("Error fetching pets:", error);
    }
  };

  // Client-side filtering function
  const applyFilters = () => {
    console.log("Applying filters:", {
      filters,
      searchTerm: debouncedSearchTerm,
    });

    let filtered = [...allPetsData];

    // Apply search filter
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (pet) =>
          pet.name?.toLowerCase().includes(searchLower) ||
          pet.breed?.toLowerCase().includes(searchLower) ||
          pet.species?.toLowerCase().includes(searchLower) ||
          pet.ownerId?.userDetails?.name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply species filter
    if (filters.species) {
      filtered = filtered.filter(
        (pet) => pet.species?.toLowerCase() === filters.species.toLowerCase()
      );
    }

    // Apply gender filter
    if (filters.gender) {
      filtered = filtered.filter(
        (pet) => pet.gender?.toLowerCase() === filters.gender.toLowerCase()
      );
    }

    // Apply breed filter
    if (filters.breed.trim()) {
      const breedLower = filters.breed.toLowerCase().trim();
      filtered = filtered.filter((pet) =>
        pet.breed?.toLowerCase().includes(breedLower)
      );
    }

    console.log(
      "Filtered pets:",
      filtered.length,
      "out of",
      allPetsData.length
    );
    setFilteredPets(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    console.log(`Changing filter ${filterType} to:`, value);
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [filterType]: value,
      };
      console.log("New filters state:", newFilters);
      return newFilters;
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleDeletePet = async (petId) => {
    if (window.confirm("Are you sure you want to delete this pet?")) {
      try {
        setDeleteLoading(true);
        await deletePet(petId);
        // Refresh the pets list after deletion
        await fetchAllPets();
      } catch (err) {
        console.error("Failed to delete pet:", err);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  const clearFilters = () => {
    console.log("Clearing all filters");
    setFilters({
      species: "",
      gender: "",
      breed: "",
    });
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPage(1);
  };

  const handleRefresh = () => {
    fetchAllPets();
  };

  const getNextVaccination = (pet) => {
    if (!pet.vaccinations || pet.vaccinations.length === 0) return null;

    const upcomingVaccinations = pet.vaccinations
      .filter((v) => v.nextDue && new Date(v.nextDue) > new Date())
      .sort((a, b) => new Date(a.nextDue) - new Date(b.nextDue));

    return upcomingVaccinations[0];
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Check if any filters are active
  const hasActiveFilters =
    searchTerm || filters.species || filters.gender || filters.breed;

  // Get current page pets for pagination
  const getCurrentPagePets = () => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredPets.slice(startIndex, endIndex);
  };

  // Calculate total pages for client-side pagination
  const totalPagesCalculated = Math.ceil(filteredPets.length / ITEMS_PER_PAGE);

  // Get current page pets
  const currentPagePets = getCurrentPagePets();

  // Species options - predefined list
  const speciesOptions = ["Dog", "Cat", "Bird", "Rabbit", "Hamster", "Fish"];

  // Show loading spinner only on initial load
  if (isInitialLoad && allPetsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Professional Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Pet Management
                </h1>
                <p className="text-gray-600 mt-1 flex items-center space-x-2">
                  <span>
                    {filteredPets.length} of {allPetsData.length}{" "}
                    {filteredPets.length === 1 ? "pet" : "pets"}
                  </span>
                  {hasActiveFilters && (
                    <>
                      <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                      <span className="text-blue-600 font-medium">
                        Filtered
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex items-center space-x-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {allPetsData.length}
                </div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  Total Pets
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {
                    allPetsData.filter((pet) =>
                      pet.vaccinations?.some(
                        (v) => v.nextDue && new Date(v.nextDue) > new Date()
                      )
                    ).length
                  }
                </div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  Vaccinated
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {new Set(allPetsData.map((pet) => pet.species)).size}
                </div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">
                  Species
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Search and Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          {/* Search Bar */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by pet name, breed, species, or owner..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="block w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  >
                    <X className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors" />
                  </button>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`relative inline-flex items-center px-4 py-3 border rounded-lg font-medium transition-all duration-200 ${
                    showFilters || hasActiveFilters
                      ? "border-blue-200 bg-blue-50 text-blue-700 shadow-sm"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {hasActiveFilters && !showFilters && (
                    <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-blue-600 rounded-full">
                      {
                        [
                          searchTerm,
                          filters.species,
                          filters.gender,
                          filters.breed,
                        ].filter(Boolean).length
                      }
                    </span>
                  )}
                </button>

                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center px-4 py-3 border border-red-200 bg-red-50 text-red-700 rounded-lg font-medium hover:bg-red-100 transition-colors"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </button>
                )}

                <button
                  onClick={handleRefresh}
                  disabled={allPetsLoading}
                  className="inline-flex items-center px-4 py-3 border border-gray-300 bg-white text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    className={`w-4 h-4 mr-2 ${
                      allPetsLoading ? "animate-spin" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && !showFilters && (
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-700 mr-2">
                  Active filters:
                </span>
                {searchTerm && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full font-medium">
                    Search: "{searchTerm}"
                    <button
                      onClick={() => setSearchTerm("")}
                      className="hover:text-blue-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.species && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full font-medium">
                    Species: {filters.species}
                    <button
                      onClick={() => handleFilterChange("species", "")}
                      className="hover:text-green-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.gender && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full font-medium">
                    Gender: {filters.gender}
                    <button
                      onClick={() => handleFilterChange("gender", "")}
                      className="hover:text-purple-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filters.breed && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full font-medium">
                    Breed: {filters.breed}
                    <button
                      onClick={() => handleFilterChange("breed", "")}
                      className="hover:text-orange-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Extended Filters */}
          {showFilters && (
            <div className="p-6 bg-gray-50 border-b border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Species
                  </label>
                  <select
                    value={filters.species}
                    onChange={(e) =>
                      handleFilterChange("species", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <option value="">All Species</option>
                    {speciesOptions.map((species) => (
                      <option key={species} value={species.toLowerCase()}>
                        {species}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Gender
                  </label>
                  <select
                    value={filters.gender}
                    onChange={(e) =>
                      handleFilterChange("gender", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <option value="">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Breed
                  </label>
                  <input
                    type="text"
                    placeholder="Enter breed to filter"
                    value={filters.breed}
                    onChange={(e) =>
                      handleFilterChange("breed", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={clearFilters}
                  className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-2 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear all filters
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {allPetsError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg
                className="w-4 h-4 text-red-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Error loading pets
              </h3>
              <p className="text-sm text-red-700 mt-1">{allPetsError}</p>
            </div>
          </div>
        )}

        {/* Loading Indicator */}
        {allPetsLoading && !isInitialLoad && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
            <span className="text-blue-700 font-medium">
              Updating pets list...
            </span>
          </div>
        )}

        {/* Enhanced Pets Grid */}
        {filteredPets.length === 0 && !allPetsLoading && !isInitialLoad ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {hasActiveFilters
                ? "No pets match your criteria"
                : "No pets found"}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {hasActiveFilters
                ? "Try adjusting your search criteria or filters to find what you're looking for."
                : "Get started by adding your first pet to the system."}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {currentPagePets.map((pet) => {
              const nextVaccination = getNextVaccination(pet);

              return (
                <div
                  key={pet._id}
                  className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 overflow-hidden group"
                >
                  {/* Enhanced Pet Image */}
                  <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                    {pet.image ? (
                      <img
                        src={pet.image}
                        alt={pet.name || "Pet"}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Camera className="h-12 w-12 text-gray-400" />
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => handleDeletePet(pet._id)}
                        disabled={deleteLoading}
                        className="w-8 h-8 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors disabled:opacity-50 shadow-lg flex items-center justify-center"
                        title="Delete pet"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        className="w-8 h-8 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors shadow-lg flex items-center justify-center"
                        title="Edit pet"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Status Indicators */}
                    <div className="absolute top-3 left-3 flex gap-2">
                      {pet.gender && (
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            pet.gender === "male"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-pink-100 text-pink-800"
                          }`}
                        >
                          {pet.gender.charAt(0).toUpperCase() +
                            pet.gender.slice(1)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Enhanced Pet Info */}
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-lg font-bold text-gray-900 truncate">
                        {pet.name || "Unnamed Pet"}
                      </h3>
                    </div>

                    {/* Pet Details Grid */}
                    <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                      <div>
                        <span className="text-gray-500 block">Species</span>
                        <span className="font-medium text-gray-900">
                          {pet.species || "Unknown"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Breed</span>
                        <span className="font-medium text-gray-900">
                          {pet.breed || "Mixed"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Age</span>
                        <span className="font-medium text-gray-900">
                          {pet.dateOfBirth
                            ? (() => {
                                const today = new Date();
                                const birthDate = new Date(pet.dateOfBirth);

                                // Difference in ms → days
                                const diffMs = today - birthDate;
                                const diffDays = Math.floor(
                                  diffMs / (1000 * 60 * 60 * 24)
                                );
                                const diffMonths =
                                  today.getMonth() -
                                  birthDate.getMonth() +
                                  12 *
                                    (today.getFullYear() -
                                      birthDate.getFullYear());
                                const diffYears =
                                  today.getFullYear() - birthDate.getFullYear();

                                // Adjust years correctly
                                let ageYears = diffYears;
                                if (
                                  today.getMonth() < birthDate.getMonth() ||
                                  (today.getMonth() === birthDate.getMonth() &&
                                    today.getDate() < birthDate.getDate())
                                ) {
                                  ageYears--;
                                }

                                if (ageYears >= 1) {
                                  return `${ageYears} Year`;
                                } else if (diffMonths >= 1) {
                                  return `${diffMonths} Month`;
                                } else {
                                  return `${diffDays} Days`;
                                }
                              })()
                            : "Unknown"}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Weight</span>
                        <span className="font-medium text-gray-900">
                          {pet.weight ? `${pet.weight}kg` : "Unknown"}
                        </span>
                      </div>
                    </div>

                    {/* Owner Info */}
                    {pet.ownerId && (
                      <div className="border-t border-gray-100 pt-3 mb-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <MapPin className="w-4 h-4" />
                          <span className="font-medium">Owner</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900">
                          {pet.ownerId.userDetails?.name || "Unknown Owner"}
                        </p>
                      </div>
                    )}

                    {/* Health Status */}
                    {/* <div className="border-t border-gray-100 pt-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <Calendar className="w-4 h-4" />
                        <span className="font-medium">Health Status</span>
                      </div>

                      <div className="space-y-1">
                        {pet.medicalHistory && pet.medicalHistory.length > 0 ? (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                            <span className="text-xs text-amber-700 font-medium">
                              Recent:{" "}
                              {
                                pet.medicalHistory[
                                  pet.medicalHistory.length - 1
                                ].condition
                              }
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            <span className="text-xs text-green-700 font-medium">
                              No medical history
                            </span>
                          </div>
                        )}

                        {nextVaccination ? (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                            <span className="text-xs text-blue-700 font-medium">
                              Next: {nextVaccination.vaccine} -{" "}
                              {formatDate(nextVaccination.nextDue)}
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                            <span className="text-xs text-gray-500">
                              No upcoming vaccinations
                            </span>
                          </div>
                        )}
                      </div>
                    </div> */}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Enhanced Pagination */}
        {totalPagesCalculated > 1 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-700">
              Showing{" "}
              <span className="font-medium">
                {(page - 1) * ITEMS_PER_PAGE + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(page * ITEMS_PER_PAGE, filteredPets.length)}
              </span>{" "}
              of <span className="font-medium">{filteredPets.length}</span>{" "}
              results
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>

              <div className="flex gap-1">
                {Array.from(
                  { length: Math.min(totalPagesCalculated, 5) },
                  (_, i) => {
                    let pageNum;
                    if (totalPagesCalculated <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPagesCalculated - 2) {
                      pageNum = totalPagesCalculated - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          page === pageNum
                            ? "bg-blue-600 text-white border border-blue-600"
                            : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  }
                )}
              </div>

              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPagesCalculated}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllPets;
